import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import usePageTitle from '../../hooks/UsePageTitle';

const LegalDueDiligence = () => {
  usePageTitle('Legal Due Diligence');

  return (
    <div className="page-container">
      <div className="grid">
        <div className="col-12">
          <h1 className="text-center">Legal Due Diligence</h1>
          <Divider />
        </div>

        {/* Introduction Section */}
        <div className="col-12 md:col-6">
          <img 
            src='/images/Legal Due Diligence.jpg' 
            alt="Legal Due Diligence" 
            className="w-full border-round shadow-2 mb-4" 
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <p className="text-lg line-height-3">
              Whether you're acquiring a business, entering into a joint venture, investing in a startup, 
              or restructuring internal operations, Legal Due Diligence (LDD) is a critical checkpoint 
              that ensures your decisions are rooted in compliance, risk-awareness, and legal clarity.
            </p>
            <p className="text-lg line-height-3 mt-3">
              At SnP Finvisors, our Legal Due Diligence services are designed to help clients make informed 
              decisions by identifying hidden liabilities, potential disputes, regulatory gaps, and contract 
              risks — before they become problems.
            </p>
            <p className="text-lg line-height-3 mt-3 font-semibold text-primary">
              We believe legal due diligence is not just a formality — it's a safeguard, a lens through 
              which the legal health of a business is examined and decoded.
            </p>
          </Card>
        </div>

        {/* What is Legal Due Diligence Section */}
        <div className="col-12 mt-5">
          <h2>What is Legal Due Diligence?</h2>
          <Divider />
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <p className="text-xl line-height-3">
                Legal due diligence involves a comprehensive review of all legal aspects of an entity — 
                its ownership, contracts, statutory filings, ongoing litigation, intellectual property, 
                employment practices, regulatory registrations, and more. This analysis reveals potential 
                red flags, legal non-compliances, and risks that could impact valuation, deal structure, 
                or future liabilities.
              </p>
              <p className="text-xl line-height-3 mt-3">
                It is especially important during:
              </p>
              <ul className="text-xl line-height-3 ml-4">
                <li className="mb-2">Mergers & acquisitions (M&A)</li>
                <li className="mb-2">Joint ventures & strategic partnerships</li>
                <li className="mb-2">PE/VC investments</li>
                <li className="mb-2">Business restructuring</li>
                <li>Legal risk assessments or audits</li>
              </ul>
            </div>
            <img 
              src='/images/legal2.jpg' 
              alt="Legal Due Diligence Process" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* Framework Section */}
        <div className="col-12 mt-5">
          <h2>Our Legal Due Diligence Framework</h2>
          <p className="text-xl mb-4">
            At SnP Finvisors, we follow a thorough and structured process tailored to the nature 
            of the transaction and the industry involved:
          </p>
          <Divider />
          
          <div className="grid">
            {/* Framework Item 1 */}
            <div className="col-12">
              <Card title="1. Entity & Corporate Structure Verification" className="mb-4">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Validation of legal existence, type of incorporation, shareholding pattern, and governing documents.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of Memorandum & Articles of Association, partnership deeds, LLP agreements, and amendments.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Check for compliance with Companies Act, LLP Act, Partnership Act, and local statutes.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 2 */}
            <div className="col-12">
              <Card title="2. Statutory & Regulatory Compliance Review" className="mb-4">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Scrutiny of licenses, permits, approvals, and certificates (FSSAI, GST, Shops & Establishment, Fire NOC, etc.).</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of sector-specific compliance (education, healthcare, infrastructure, NBFCs, etc.).</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Analysis of ROC filings, annual returns, board resolutions, and secretarial records.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 3 */}
            <div className="col-12">
              <Card title="3. Contracts & Agreements Review" className="mb-4">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Vetting of key contracts including lease deeds, vendor agreements, loan covenants, franchise/MoUs, etc.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of termination clauses, indemnities, arbitration provisions, and change-of-control clauses.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identification of contract breaches, expired documents, or contingent liabilities.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 4 */}
            <div className="col-12">
              <Card title="4. Litigation & Dispute Review" className="mb-4">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Detailed status of ongoing or past litigation (civil, criminal, labor, tax, IP-related, etc.).</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Assessment of legal exposure, probable loss, and pending penalties or damages.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Verification of whether disputes are disclosed, defended, or likely to affect valuation.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 5 */}
            <div className="col-12">
              <Card title="5. Employment & Labor Compliance" className="mb-4">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of employment contracts, ESOP policies, HR policies, and non-compete/NDA clauses.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Check for PF, ESI, Gratuity, Bonus Act compliance and contractor labor laws.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Verification of compliance with maternity laws, minimum wages, and statutory registers.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 6 */}
            <div className="col-12">
              <Card title="6. Intellectual Property (IP) & Branding" className="mb-4">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Ownership verification of trademarks, copyrights, patents, domain names, and proprietary technology.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of license agreements, brand assignments, and IP registration status.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Protection of trade secrets, digital assets, and confidential data.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 7 */}
            <div className="col-12">
              <Card title="7. Property & Asset Verification" className="mb-4">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Title diligence on land, buildings, or long-term leases.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Scrutiny of encumbrances, charges, mortgages, and asset ownership documentation.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of insurance policies and coverage for physical and intangible assets.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 8 */}
            <div className="col-12 md:col-6">
              <Card title="8. Environmental, Safety & Industry-Specific Laws" className="mb-4">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>For industries like manufacturing or healthcare — verification of pollution control licenses, factory act compliance, biomedical waste handling, etc.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Evaluation of exposure to sectoral legal risks and penalties.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 9 */}
            <div className="col-12 md:col-6">
              <Card title="9. Legal Risk Flagging & Deal Impact" className="mb-4">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Summary of red flags, material risks, and recommended mitigations.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Legal risk scoring and mapping to financial liabilities, indemnity needs, or deal restructuring advice.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Tailored legal inputs for SPAs, shareholder agreements, or JV documents.</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>

        {/* Closing Section */}
        <div className="col-12 mt-5">
          <div className="flex flex-column md:flex-row gap-5">
            <img 
              src='/images/legal3.jpg' 
              alt="Legal Due Diligence Solutions" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
            <div className="flex-1">
              <h2>Comprehensive Legal Insight for Confident Decision-Making</h2>
              <Divider />
              <p className="text-xl line-height-3">
                At SnP Finvisors, we transform legal due diligence from a compliance exercise into a strategic 
                advantage. Our meticulous approach ensures you have complete visibility into legal risks and 
                opportunities, empowering you to structure deals with confidence and safeguard your investments.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalDueDiligence;